import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, Filter, ExternalLink, MessagesSquare } from "lucide-react";
import AdminLayout from "../../admin/AdminLayout";
import AdminAuthGuard from "../../admin/AdminAuthGuard";
import SendbirdChannelPanel from "../../components/sendbird/SendbirdChannelPanel";
import { api } from "../../api/client";

const STATUS_PILL = {
    pending_review: "bg-amber-100 text-amber-700 ring-amber-200",
    approved: "bg-emerald-100 text-emerald-700 ring-emerald-200",
    rejected: "bg-rose-100 text-rose-600 ring-rose-200",
};

const STATUSES = [
    { value: "pending_review", label: "Pending" },
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
    { value: "", label: "All" },
];

const Content = () => {
    const [params, setParams] = useSearchParams();
    const initialStatus = params.get("status") || "pending_review";
    const [section, setSection] = useState(params.get("section") === "chats" ? "chats" : "queue");
    const [status, setStatus] = useState(initialStatus);
    const [q, setQ] = useState("");
    const [page, setPage] = useState(1);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/admin/prescriptions", {
                params: {
                    status: status || undefined,
                    q: q.trim() || undefined,
                    page,
                    page_size: 20,
                },
            });
            setRows(r.data.items);
            setTotal(r.data.total);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, page]);

    const onStatusChange = (s) => {
        setStatus(s);
        setPage(1);
        const next = new URLSearchParams(params);
        if (s) next.set("status", s); else next.delete("status");
        setParams(next, { replace: true });
    };

    return (
        <div className="space-y-5" data-testid="admin-prescriptions-page">
            {/* Section tabs — Queue vs. Live Chats */}
            <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1 shadow-sm" data-testid="rx-section-tabs">
                <button
                    onClick={() => setSection("queue")}
                    className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                        section === "queue" ? "bg-brand-700 text-white shadow-sm" : "text-slate-600 hover:bg-slate-50"
                    }`}
                    data-testid="pharmacist-rx-queue-tab"
                >
                    <Filter className="h-3.5 w-3.5" /> Rx Queue
                </button>
                <button
                    onClick={() => setSection("chats")}
                    className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                        section === "chats" ? "bg-brand-700 text-white shadow-sm" : "text-slate-600 hover:bg-slate-50"
                    }`}
                    data-testid="pharmacist-rx-live-chats-tab"
                >
                    <MessagesSquare className="h-3.5 w-3.5" /> Live Rx Chats
                </button>
            </div>

            {section === "chats" ? (
                <SendbirdChannelPanel
                    customType="rx_verification"
                    claimEndpoint={(ch) => {
                        // channel_url is `pills_rx_{prescription_id}`; extract the tail
                        const parts = (ch?.url || "").split("_");
                        const rxId = parts.slice(2).join("_");
                        return `/sendbird/channels/rx/${rxId}/claim`;
                    }}
                    claimButtonLabel="Claim & respond"
                    testId="pharmacist-rx"
                    height="calc(100vh - 14rem)"
                />
            ) : (
            <>
            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3">
                <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1 shadow-sm" data-testid="rx-status-tabs">
                    {STATUSES.map((s) => (
                        <button
                            key={s.value}
                            onClick={() => onStatusChange(s.value)}
                            className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                                status === s.value ? "bg-brand-700 text-white shadow-sm" : "text-slate-600 hover:bg-slate-50"
                            }`}
                            data-testid={`rx-status-${s.value || "all"}`}
                        >
                            {s.label}
                        </button>
                    ))}
                </div>

                <form
                    onSubmit={(e) => { e.preventDefault(); setPage(1); load(); }}
                    className="relative ml-auto flex-1 min-w-[240px] max-w-md"
                >
                    <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search by customer name or email"
                        className="input-clean !rounded-lg pl-10"
                        data-testid="rx-search-input"
                    />
                </form>
            </div>

            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-soft" data-testid="rx-table-wrapper">
                <table className="min-w-full divide-y divide-slate-100 text-sm">
                    <thead className="bg-slate-50/80 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                        <tr>
                            <th className="px-4 py-3 text-left">Customer</th>
                            <th className="px-4 py-3 text-left">Doctor</th>
                            <th className="px-4 py-3 text-left">Date</th>
                            <th className="px-4 py-3 text-left">OCR</th>
                            <th className="px-4 py-3 text-left">Status</th>
                            <th className="px-4 py-3" />
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {loading ? (
                            [0,1,2,3,4].map((i) => (
                                <tr key={i}><td colSpan={6} className="px-4 py-3"><div className="h-6 skeleton rounded" /></td></tr>
                            ))
                        ) : rows.length === 0 ? (
                            <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-slate-400">No prescriptions match these filters.</td></tr>
                        ) : (
                            rows.map((rx) => (
                                <tr key={rx.id} className="hover:bg-slate-50" data-testid={`rx-row-${rx.id}`}>
                                    <td className="px-4 py-3">
                                        <p className="font-medium text-slate-900">{rx.user.name}</p>
                                        <p className="text-xs text-slate-500">{rx.user.email}</p>
                                    </td>
                                    <td className="px-4 py-3 text-slate-700">{rx.doctor_name || "—"}</td>
                                    <td className="px-4 py-3 text-slate-700">
                                        {new Date(rx.uploaded_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-slate-600">
                                            {rx.ocr_status}
                                        </span>
                                        {rx.confidence_score != null && (
                                            <span className="ml-1 text-[11px] text-slate-500">
                                                {Math.round((rx.confidence_score || 0) * 100)}%
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ring-1 ${STATUS_PILL[rx.verification_status]}`} data-testid={`rx-status-pill-${rx.id}`}>
                                            {rx.verification_status.replace("_", " ")}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <Link
                                            to={`/admin/prescriptions/${rx.id}`}
                                            className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-700 hover:border-brand-300 hover:text-brand-700"
                                            data-testid={`rx-open-${rx.id}`}
                                        >
                                            Open <ExternalLink className="h-3 w-3" />
                                        </Link>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
                {/* Pagination */}
                {!loading && total > 20 && (
                    <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3 text-xs text-slate-500">
                        <span>
                            Page {page} · {Math.min(page * 20, total)} of {total}
                        </span>
                        <div className="flex gap-2">
                            <button disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="rounded-md border border-slate-200 px-3 py-1 disabled:opacity-50">Prev</button>
                            <button disabled={page * 20 >= total} onClick={() => setPage((p) => p + 1)} className="rounded-md border border-slate-200 px-3 py-1 disabled:opacity-50">Next</button>
                        </div>
                    </div>
                )}
            </div>
            </>
            )}
        </div>
    );
};

const AdminPrescriptions = () => (
    <AdminAuthGuard>
        <AdminLayout title="Prescriptions" subtitle="Pharmacist verification workspace">
            <Content />
        </AdminLayout>
    </AdminAuthGuard>
);

export default AdminPrescriptions;
